import { Button, Divider, Paper } from '@material-ui/core';
import Breadcrumbs from '@material-ui/core/Breadcrumbs';
import moment from 'moment-timezone';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import { Alert } from '@material-ui/lab';
import humanizeDuration from 'humanize-duration';
import { withSnackbar, WithSnackbarProps } from 'notistack';
import React, { useContext, useEffect, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import WindsorApi from '../../api/WindsorApi';
import Gravatar from '../../common/Gravatar';
import LinkRouter from '../../common/LinkRouter';
import LoadingButton from '../../common/LoadingButton';
import LoadingIndicator from '../../common/LoadingIndicator';
import MiddleDot from '../../common/MiddleDot';
import FaroDeckLink from '../../faro/decks/FaroDeckLink';
import FaroDeploymentLink from '../../faro/deployments/FaroDeploymentLink';
import MobxContext from '../../mobx/MobxContext';
import ExecutionActionStageIcons from './ExecutionActionStageIcons';
import ExecutionActionStatusChip from './ExecutionActionStatusChip';
import ExecutionConstants, { ExecutionStatus } from './ExecutionConstants';
import ExecutionDeploymentActionStateParser from './ExecutionDeploymentActionStateParser';
import ExecutionStateParser from './ExecutionStateParser';
import ExecutionStatusIcon from './ExecutionStatusIcon';
import ExecutionTestActionStateParser from './ExecutionTestActionStateParser';
import { red } from '@material-ui/core/colors';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            maxWidth: 1200,
            margin: 'auto'
        },
        divider: {
            marginBottom: 16,
            marginTop: 8
        },
        title: {
            display: 'flex ',
            marginBottom: 8
        },
        subtitle: {
            marginBottom: 16
        },
        subtitleTypography: {
            fontWeight: 500
        },
        actionButtonsContainer: {
            display: 'flex',
            alignItems: 'start'
        },
        executionRequesterPanel: {
            border: `1px solid ${theme.palette.divider}`,
            padding: 16,
            marginBottom: 16,
            display: 'flex',
            alignItems: 'center'
        },
        executionStatusPanel: {
            border: `1px solid ${theme.palette.divider}`,
            padding: 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
        },
        gravatar: {
            width: 24,
            height: 24,
            margin: '0 4px 0 8px'
        },
        alert: {
            marginTop: 16,
            marginBottom: 16
        },
        workflowLink: {
            margin: '0 8px'
        },
        executionStatusDescriptionText: {
            marginLeft: 8
        },
        approvalLink: {
            marginLeft: 16
        },
        runExecutionButton: {
            marginLeft: 24
        },
        cancelExecutionButton: {
            marginLeft: 24
        },
        forceCancelExecutionButton: {
            marginLeft: 24,
            color: red[500],
            borderColor: red[500]
        },
        section: {
            marginBottom: 24
        },
        executionStatusDescription: {
            display: 'flex',
            alignItems: 'center'
        },
        executionStatusActions: {
            display: 'flex'
        }
    })
);

const windsorApi = new WindsorApi();

function ExecutionDetailsPage(props: RouteComponentProps<any> & WithSnackbarProps) {
    const { match, history, enqueueSnackbar } = props;
    const familyId = match.params.familyId;
    const executionId = match.params.executionId;

    const classes = useStyles({});

    const [execution, setExecution] = useState(null);
    const [workflowVersion, setWorkflowVersion] = useState(null);
    const [executionState, setExecutionState] = useState(null);
    const [deploymentActionsMap, setDeploymentActionsMap] = useState(null);
    const [testActionsMap, setTestActionsMap] = useState(null);
    const [actionStateMap, setActionStateMap] = useState(null);
    const [selectedActions, setSelectedActions] = useState(null);
    const [cancellationInProgress, setCancellationInProgress] = useState(false);
    const [forceCancelAvailable, setForceCancelAvailable] = useState(false);
    const [startRunInProgress, setStartRunInProgress] = useState(false);
    const [serverErrors, setServerErrors] = useState(null);
    const { executionStore } = useContext(MobxContext);

    useEffect(() => {
        windsorApi.getExecution(familyId, executionId).then(execution => {
            setExecution(execution);
            setWorkflowVersion(execution.WorkflowCache.Versions ? execution.WorkflowCache.Versions[execution.WorkflowCache.Versions.length - 1]['timestamp'] : 'unknown');
            if (execution.ApprovalRecord?.SelectedActions && execution.ApprovalRecord?.SelectedActions.length) {
                setSelectedActions(execution.ApprovalRecord.SelectedActions.reduce((accumulator, item) => {
                    accumulator.add(item);
                    return accumulator;
                }, new Set()));
            } else {
                setSelectedActions([...execution.WorkflowCache.DeploymentRecipe, ...execution.WorkflowCache.TestFunctions]
                    .reduce((accumulator, item) => {
                        accumulator.add(item.ActionName);
                        return accumulator;
                    }, new Set()));
            }
        });
    }, [familyId, executionId]);

    useEffect(() => {
        if (executionState && executionState.status === ExecutionStatus.InProgress) {
            const timeoutHandle = setTimeout(() => windsorApi.getExecution(familyId, executionId).then(execution => {
                setExecution(execution);
            }), 20000);
            return () => clearTimeout(timeoutHandle);
        }
    }, [executionState, familyId, executionId]);

    useEffect(() => {
        if (cancellationInProgress &&
            (executionState === ExecutionStatus.Cancelled ||
                executionState === ExecutionStatus.Failed ||
                executionState === ExecutionStatus.Passed ||
                executionState === ExecutionStatus.PassedWithWarnings)) {
            setCancellationInProgress(false);
        }
    }, [cancellationInProgress, executionState]);

    useEffect(() => {
        if (execution) {
            setExecutionState(new ExecutionStateParser().parseState(execution));

            setDeploymentActionsMap(execution.DeploymentList.reduce((acc, item) => {
                acc[item.ActionName] = item;
                return acc;
            }, {}));

            setTestActionsMap(execution.TestSets.reduce((acc, item) => {
                acc[item.ActionName] = {
                    startTime: parseInt(item.StartTime),
                    endTime: parseInt(item.EndTime),
                    totalPassed: item.TotalPassed,
                    totalTests: item.Tests.length
                };
                return acc;
            }, {}));

            const deploymentActionStateParser = new ExecutionDeploymentActionStateParser(execution);
            const actionStates = execution.WorkflowCache.DeploymentRecipe.reduce((acc, item) => {
                acc[item.ActionName] = deploymentActionStateParser.getActionState(item.ActionName);
                return acc;
            }, {});

            if (execution.ExecutionPlan !== 'TEARDOWN_ALL') {
                // Set state map for test actions
                const testActionStateParser = new ExecutionTestActionStateParser(execution);
                execution.WorkflowCache.TestFunctions.forEach(action => {
                    actionStates[action.ActionName] = testActionStateParser.getActionState(action.ActionName);
                });
            }

            setActionStateMap(actionStates);
        }
    }, [execution]);

    useEffect(() => {
        if (cancellationInProgress) {
            const timeoutId = setTimeout(() => {
                setForceCancelAvailable(true);
            }, 10000); // 10-second delay

            // Cleanup timeout on unmount to prevent memory leaks
            return () => clearTimeout(timeoutId);
        }
    }, [cancellationInProgress])

    const onRunExecution = () => {
        setStartRunInProgress(true);
        executionStore.runExecution(familyId, executionId)
            .then((execution) => {
                setStartRunInProgress(false);
                enqueueSnackbar('Execution started');
                setExecution(execution);
            });
    };

    const onCancelExecution = () => {
        setCancellationInProgress(true);
        executionStore.cancelExecution(familyId, executionId)
            .then((execution) => {
                setExecution(execution);
            }).catch(error => {
                setServerErrors(error.data.errorMessage);
                setCancellationInProgress(false);
            });
    };

    const onForceCancelExecution = () => {
        executionStore.forceCancelExecution(familyId, executionId)
            .then((execution) => {
                setExecution(execution);
            }).catch(error => {
                setServerErrors(error.data.errorMessage);
            });
    };

    const showForceCancel = cancellationInProgress && forceCancelAvailable;

    return execution && executionState && deploymentActionsMap && testActionsMap && actionStateMap && selectedActions ? (
        <div className={classes.root}>
            <Breadcrumbs aria-label="breadcrumb">
                <LinkRouter color="textSecondary" to={`/families`}>
                    Families
                </LinkRouter>
                <LinkRouter color="textSecondary" to={`/families/${familyId}`}>
                    {execution.FamilyCache.Name}
                </LinkRouter>
                <LinkRouter color="textSecondary" to={`/families/${familyId}/workflows`}>
                    Workflows
                </LinkRouter>
                <LinkRouter color="textSecondary" to={`/families/${familyId}/workflows/${execution.WorkflowCache.GUID}`}>
                    {execution.WorkflowName}
                </LinkRouter>
                <LinkRouter color="textSecondary" to={`/families/${familyId}/workflows/${execution.WorkflowCache.GUID}?tab=executions`}>
                    Executions
                </LinkRouter>
                <Typography color="textPrimary">{execution.GUID}</Typography>
            </Breadcrumbs>
            <Divider className={classes.divider} />
            <div className={classes.section}>
                <div className={classes.title}>
                    <Typography variant="h6">
                        Execution of workflow
                    </Typography>
                    <LinkRouter className={classes.workflowLink} variant="h6" to={`/families/${familyId}/workflows/${execution.WorkflowCache.GUID}`}>{execution.WorkflowName}</LinkRouter>
                    <Typography variant="h6">
                        version
                    </Typography>
                    <LinkRouter className={classes.workflowLink} variant="h6" to={`/families/${familyId}/workflows/${execution.WorkflowCache.GUID}?version=${workflowVersion}`}>{workflowVersion}</LinkRouter>
                </div>
                <div className={classes.subtitle}>
                    <Typography className={classes.subtitleTypography} color="primary" display="inline" >
                        Family: {execution.FamilyCache.Name}
                    </Typography>
                    <MiddleDot />
                    <Typography className={classes.subtitleTypography} color="primary" display="inline" >
                        Environment: {execution.AccountCache.EnvironmentName}
                    </Typography>
                    <MiddleDot />
                    <Typography className={classes.subtitleTypography} color="primary" display="inline" >
                        Region: {execution.AccountCache.Region}
                    </Typography>
                </div>

                <div className={classes.executionRequesterPanel}>
                    <Typography variant="subtitle1">{ExecutionConstants.ExecutionPlanLabels[execution.ExecutionPlan]} triggered {moment.unix(parseInt(execution.StartTimeStamp)).fromNow()} by</Typography>
                    <Gravatar className={classes.gravatar} email={execution.ApprovalRecord?.Requester ?? 'unknown'} />
                    <Typography variant="subtitle1">{execution.ApprovalRecord?.Requester}</Typography>
                </div>
                <div className={classes.executionStatusPanel}>
                    <div className={classes.executionStatusDescription}>
                        <ExecutionStatusIcon type={executionState.status} />
                        <Typography className={classes.executionStatusDescriptionText} variant="subtitle1">{executionState.description}</Typography>
                        {executionState.status === ExecutionStatus.AwaitingApprovals && <div className={classes.approvalLink}>
                            <LinkRouter variant="body1" to={`/approvals/${execution.ApprovalRecord.GUID}`}>View Approval Request</LinkRouter>
                        </div>}
                    </div>
                    <div className={classes.executionStatusActions}>
                        {(executionState.status === ExecutionStatus.Ready || executionState.status === ExecutionStatus.Scheduled || executionState.status === ExecutionStatus.InProgress)
                            && !showForceCancel && <LoadingButton
                                disabled={cancellationInProgress}
                                loading={cancellationInProgress}
                                className={classes.cancelExecutionButton}
                                color="default"
                                variant="outlined"
                                onClick={onCancelExecution}>
                                Cancel Execution
                            </LoadingButton>}
                        {executionState.status === ExecutionStatus.InProgress
                            && showForceCancel && <Button
                                className={classes.forceCancelExecutionButton}
                                color="primary"
                                variant="outlined"
                                onClick={onForceCancelExecution}>
                                Force Cancel Execution
                            </Button>}
                        {executionState.status === ExecutionStatus.Ready && <div className={classes.runExecutionButton}>
                            <LoadingButton
                                loading={startRunInProgress}
                                color="primary"
                                variant="outlined"
                                onClick={onRunExecution}>
                                Run Execution
                            </LoadingButton>
                        </div>}
                    </div>
                </div>
                {execution.Errors && execution.Errors.length > 0 && <Alert severity="error" className={classes.alert}>
                    {Array.isArray(execution.Errors) ? execution.Errors.map((error, index) => <Typography key={index}>{error}</Typography>)
                        : <Typography>{execution.Errors}</Typography>}
                </Alert>}
            </div>
            {serverErrors && <Alert severity="warning" className={classes.alert}>
                {serverErrors.map((error, index) => <Typography key={index}>{error}</Typography>)}
            </Alert>}
            {execution.WorkflowCache.DeploymentRecipe.length > 0 && <div className={classes.section}>
                <Typography variant="h6" color="textSecondary" gutterBottom>Deployment Actions</Typography>
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Status</TableCell>
                                <TableCell>Action</TableCell>
                                <TableCell>Deployment</TableCell>
                                <TableCell>Deck</TableCell>
                                <TableCell>Started</TableCell>
                                <TableCell>Run Time</TableCell>
                                <TableCell>Stages</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {execution.WorkflowCache.DeploymentRecipe.filter(item => selectedActions.has(item.ActionName)).map(row => (
                                <TableRow key={row.ActionName}>
                                    <TableCell>
                                        <ExecutionActionStatusChip state={actionStateMap[row.ActionName]} chipProps={{
                                            size: "small",
                                            onClick: () => history.push(`/families/${familyId}/executions/${executionId}/deploymentactions/${row.ActionName}`)
                                        }} />
                                    </TableCell>
                                    <TableCell>
                                        <LinkRouter variant="body1" to={`/families/${familyId}/executions/${executionId}/deploymentactions/${row.ActionName}`}>{row.ActionName}</LinkRouter>
                                    </TableCell>
                                    <TableCell>
                                        <FaroDeploymentLink
                                            variant="body1"
                                            deploymentName={row.DeploymentName}
                                            accountId={execution.AccountCache.AccountID}
                                            region={execution.AccountCache.Region}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            <FaroDeckLink
                                                variant="body1"
                                                deckName={row.DeckName}
                                                version={row.Version}
                                            />
                                        </Typography>
                                        <Typography variant="caption">{row.Version}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        {deploymentActionsMap[row.ActionName] && deploymentActionsMap[row.ActionName].StartTime ?
                                            moment.unix(parseInt(deploymentActionsMap[row.ActionName].StartTime)).fromNow() : 'Not Started'}
                                    </TableCell>
                                    <TableCell>
                                        {deploymentActionsMap[row.ActionName] && deploymentActionsMap[row.ActionName].EndTime ?
                                            humanizeDuration((parseInt(deploymentActionsMap[row.ActionName].EndTime) - parseInt(deploymentActionsMap[row.ActionName].StartTime)) * 1000) : 'Not Started'}
                                    </TableCell>
                                    <TableCell>
                                        <ExecutionActionStageIcons
                                            executionPlan={execution.ExecutionPlan}
                                            deploymentList={execution.DeploymentList}
                                            executionStatus={executionState.status}
                                            actionName={row.ActionName}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>}
            {execution.ExecutionPlan !== 'TEARDOWN_ALL' && execution.WorkflowCache.TestFunctions.length > 0 && <div className={classes.section}>
                <Typography variant="h6" color="textSecondary" gutterBottom>Tests</Typography>
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Status</TableCell>
                                <TableCell>Action</TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell>Version</TableCell>
                                <TableCell>Started</TableCell>
                                <TableCell>Run Time</TableCell>
                                <TableCell>Results</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {execution.WorkflowCache.TestFunctions.filter(item => selectedActions.has(item.ActionName)).map(row => (
                                <TableRow key={row.ActionName}>
                                    <TableCell>
                                        <ExecutionActionStatusChip state={actionStateMap[row.ActionName]} chipProps={{
                                            size: "small",
                                            onClick: () => history.push(`/families/${familyId}/executions/${executionId}/testactions/${row.ActionName}`)
                                        }} />
                                    </TableCell>
                                    <TableCell>
                                        <LinkRouter variant="body1" to={`/families/${familyId}/executions/${executionId}/testactions/${row.ActionName}`}>{row.ActionName}</LinkRouter>
                                    </TableCell>
                                    <TableCell>
                                        {row.TestName}
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{row.Version}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        {testActionsMap[row.ActionName] && testActionsMap[row.ActionName].startTime ?
                                            moment.unix(testActionsMap[row.ActionName].startTime).fromNow() : 'Not Started'}
                                    </TableCell>
                                    <TableCell>
                                        {testActionsMap[row.ActionName] && testActionsMap[row.ActionName].endTime ?
                                            humanizeDuration((testActionsMap[row.ActionName].endTime - testActionsMap[row.ActionName].startTime) * 1000) : 'Not Started'}
                                    </TableCell>
                                    <TableCell>
                                        {testActionsMap[row.ActionName] ? `${testActionsMap[row.ActionName].totalPassed} of ${testActionsMap[row.ActionName].totalTests} passed` : 'Not Started'}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>}
            <div>
                <Typography variant="h6" color="textSecondary" gutterBottom>Approval History</Typography>
                {(!execution.ApprovalRecord?.Approvals || execution.ApprovalRecord?.Approvals.length === 0) && <Typography color="textSecondary">No approvals</Typography>}
                {execution.ApprovalRecord?.Approvals && execution.ApprovalRecord?.Approvals.length > 0 && <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Approver</TableCell>
                                <TableCell>Time of Approval</TableCell>
                                <TableCell>Action Approved</TableCell>
                                <TableCell>Approval Verdict</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {execution.ApprovalRecord?.Approvals.map((approval, index) => <TableRow key={index}>
                                <TableCell>{approval.User}</TableCell>
                                <TableCell>{moment.unix(parseInt(approval.ApprovalTime)).fromNow()}</TableCell>
                                <TableCell>{approval.ActionName}</TableCell>
                                <TableCell>{approval.ApprovalValue ? 'Approved' : 'Rejected'}</TableCell>
                            </TableRow>)}
                        </TableBody>
                    </Table>
                </TableContainer>}
            </div>
        </div >
    ) : <LoadingIndicator loading={true} />;
};

export default withRouter(withSnackbar(ExecutionDetailsPage));
